import React, { Suspense } from 'react';

import { Router } from 'react-router-dom';
import ReactDOM from 'react-dom';

import { Provider } from 'react-redux';
import store from './redux/store';
import { history } from './history';

import './assets/icons/remixicon.css';
import './assets/less/yoda-theme.less';

import App from './App';

import { QueryCache, QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { showErrorNotification } from './view/cockpit/assets/useAssets';

// Generic message for otherwise-unhandled query failures. The QueryClient is
// created outside React, so `intl` is not available here; we resolve a generic
// message from the current redux language instead (defaulting to pt).
const GENERIC_QUERY_ERROR_MESSAGE: Record<string, string> = {
  pt: 'Não foi possível carregar os dados. Tente novamente.',
  en: 'Could not load the data. Please try again.',
};

// Global safety net: surface silent query failures (blank tables/lists) with a
// notification. Only queries are handled here — mutations already show their
// own success/error notifications, so a global mutation handler would double up.
const queryCache = new QueryCache({
  onError: () => {
    const language = store.getState()?.customise?.language ?? 'pt';
    showErrorNotification(
      GENERIC_QUERY_ERROR_MESSAGE[language] ?? GENERIC_QUERY_ERROR_MESSAGE.pt,
    );
  },
});

// Conservative global defaults for react-query:
// - retry: 1 instead of the default 3, to avoid retry storms on failing calls.
// - refetchOnWindowFocus: false, to avoid surprise refetches when the admin
//   switches back to the tab.
// staleTime is intentionally left at the default to not change data freshness.
const queryClient = new QueryClient({
  queryCache,
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

ReactDOM.render(
  <Suspense fallback='loading'>
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <Router history={history}>
          <App />
          <ReactQueryDevtools />
        </Router>
      </QueryClientProvider>
    </Provider>
  </Suspense>,
  document.getElementById('root'),
);
